import React, { useState, useEffect, useMemo } from 'react';
import { X, Search, ArrowRight, User, MapPin, Sparkles, AlertCircle, Info, Calendar, Clock } from 'lucide-react';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { useMyMap } from '@/features/maps/core/useMyMap';
import { useMaps } from '@/features/maps/core/useMaps';
import { calculateMap } from '@/features/calculator/logic/calculationLogic';
import { LocationSelector, LocationData } from '@/components/common/LocationSelector';
import { TimeSelector } from '@/components/common/TimeSelector';
import { InfoTooltip } from '@/components/ui/InfoTooltip';
import { EXPLANATIONS } from '@/constants/explanations';
import { toast } from '@/stores/notificationStore';
import { parseDateString } from '@/utils';
import { formatForDisplay } from '@/utils/dates';
import { FlaskLoader } from '@/components/ui/FlaskLoader';
import { MapData } from '@/features/maps/core/types/MapData';
import { CalculationInput } from '@/types/Calculator';

interface ClaimSelfMapModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export const ClaimSelfMapModal: React.FC<ClaimSelfMapModalProps> = ({ isOpen, onClose }) => {
    const { user } = useAuth();
    const { findSuggestedMaps, setAsMe } = useMyMap();
    const { maps, createMap, isLoading: isMapsLoading } = useMaps(user?.id);

    const [loading, setLoading] = useState(false);
    const [view, setView] = useState<'checking' | 'suggestions' | 'manual_select' | 'create_form'>('checking');
    const [suggestions, setSuggestions] = useState<MapData[]>([]);

    // Search query for manual saved maps lookup
    const [searchQuery, setSearchQuery] = useState('');

    // Form fields for creation fallback
    const [firstName, setFirstName] = useState('');
    const [familyName, setFamilyName] = useState('');
    const [motherName, setMotherName] = useState('');
    const [gender, setGender] = useState<'male' | 'female' | ''>('');
    const [soulRoot, setSoulRoot] = useState<'cain' | 'abel' | ''>('');
    const [birthDate, setBirthDate] = useState(''); // expects YYYY-MM-DD from date input
    const [birthTime, setBirthTime] = useState('00:00');
    const [cityName, setCityName] = useState('');
    const [birthLat, setBirthLat] = useState<number | null>(null);
    const [birthLon, setBirthLon] = useState<number | null>(null);
    const [timezone, setTimezone] = useState<string | null>(null);

    // Load initial suggestions based on Google Auth metadata name
    useEffect(() => {
        const fetchSuggestions = async () => {
            if (!isOpen || !user) return;
            setLoading(true);
            setView('checking');

            const displayName = user.user_metadata?.full_name || user.user_metadata?.name || '';
            if (!displayName) {
                setLoading(false);
                setView('create_form');
                return;
            }

            try {
                const found = await findSuggestedMaps(displayName);
                setSuggestions(found);
                if (found.length > 0) {
                    setView('suggestions');
                } else {
                    setView('create_form');
                }
            } catch (err) {
                console.error('Error in initial suggestions load:', err);
                setView('create_form');
            } finally {
                setLoading(false);
            }
        };

        fetchSuggestions();
    }, [isOpen, user, findSuggestedMaps]);

    // Pre-populate creation form with Google Auth names when transitioning
    useEffect(() => {
        if (view === 'create_form' && user) {
            const givenName = user.user_metadata?.given_name || '';
            const familyNameMeta = user.user_metadata?.family_name || '';
            const displayName = user.user_metadata?.full_name || user.user_metadata?.name || '';

            const isHebrew = (str: string) => /[\u0590-\u05FF]/.test(str);

            if (givenName && isHebrew(givenName) && !firstName) {
                setFirstName(givenName);
            }
            if (familyNameMeta && isHebrew(familyNameMeta) && !familyName) {
                setFamilyName(familyNameMeta);
            }

            // Fallback to splitting display name if we don't have separate Hebrew names
            if (!firstName && displayName && isHebrew(displayName)) {
                const parts = displayName.split(/\s+/);
                if (parts.length > 0) {
                    setFirstName(parts[0]);
                }
                if (parts.length > 1 && !familyName) {
                    setFamilyName(parts.slice(1).join(' '));
                }
            }

            // Sync default gender if present in metadata
            const metaGender = user.user_metadata?.gender;
            if (metaGender && !gender) {
                if (metaGender === 'male' || metaGender === 'm' || metaGender === 'זכר') {
                    setGender('male');
                } else if (metaGender === 'female' || metaGender === 'f' || metaGender === 'נקבה') {
                    setGender('female');
                }
            }
        }
    }, [view, user, firstName, familyName, gender]);

    // Filter saved maps for manual selection view
    const filteredMaps = useMemo(() => {
        if (!searchQuery.trim()) return maps;
        const q = searchQuery.toLowerCase().trim();
        return maps.filter(map => {
            const fullName = `${map.firstName || ''} ${map.familyName || ''}`.toLowerCase();
            return fullName.includes(q) || (map.firstName || '').toLowerCase().includes(q);
        });
    }, [maps, searchQuery]);

    // Handle claiming an existing map
    const handleClaimMap = async (map: MapData) => {
        setLoading(true);
        try {
            await setAsMe(map.id);
            toast.success(`המפה של ${map.firstName} ${map.familyName || ''} שויכה אליך כ"המפה שלי"!`);
            onClose();
        } catch (err) {
            console.error('Error claiming map:', err);
            toast.error('שגיאה בשיוך המפה. נסה שוב.');
        } finally {
            setLoading(false);
        }
    };

    // Location selection helper
    const handleLocationSelect = (loc: LocationData) => {
        setCityName(loc.name || '');
        setBirthLat(loc.lat);
        setBirthLon(loc.lon);
        if (loc.timezone) {
            setTimezone(loc.timezone);
        }
    };

    // Helper to block English input in name fields
    const handleNameChange = (setter: (v: string) => void) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const val = e.target.value;
        if (/[a-zA-Z]/.test(val)) {
            toast.error('אנא השתמש באותיות בעברית בלבד (ללא אנגלית)');
            return;
        }
        setter(val);
    };

    // Handle creating a new self-map
    const handleCreateSelfMap = async (e: React.FormEvent) => {
        e.preventDefault();

        const cleanFirstName = firstName.trim();
        const cleanFamilyName = familyName.trim();
        const cleanMotherName = motherName.trim();

        if (!cleanFirstName) {
            toast.error('שם פרטי הוא שדה חובה');
            return;
        }
        if (!cleanFamilyName) {
            toast.error('שם משפחה הוא שדה חובה');
            return;
        }
        if (!cleanMotherName) {
            toast.error('שם האם הוא שדה חובה לצורך חישובי אור מקיף');
            return;
        }
        if (!gender) {
            toast.error('יש לבחור מגדר לצורך לשון פנייה תקינה');
            return;
        }
        if (!birthDate) {
            toast.error('תאריך לידה הוא שדה חובה');
            return;
        }

        // Strict character set checks
        const hebrewRegex = /^[\u0590-\u05FF\s'-]+$/;
        if (!hebrewRegex.test(cleanFirstName)) {
            toast.error('שם פרטי: אנא השתמש באותיות בעברית בלבד (ללא אנגלית)');
            return;
        }
        if (!hebrewRegex.test(cleanFamilyName)) {
            toast.error('שם משפחה: אנא השתמש באותיות בעברית בלבד (ללא אנגלית)');
            return;
        }
        if (!hebrewRegex.test(cleanMotherName)) {
            toast.error('שם האם: אנא השתמש באותיות בעברית בלבד (ללא אנגלית)');
            return;
        }

        const formattedBirthDate = parseDateString(birthDate);
        if (!formattedBirthDate) {
            toast.error('תאריך הלידה אינו תקין');
            return;
        }

        setLoading(true);

        try {
            // 1. Prepare calculation input
            const calcInput = {
                firstName: firstName.trim(),
                familyName: familyName.trim(),
                motherName: motherName.trim(),
                birthDate: formattedBirthDate,
                birthTime: birthTime || undefined,
                cityName: cityName || undefined,
                lat: birthLat || undefined,
                lng: birthLon || undefined,
                timezone: timezone || undefined,
                gender: gender || undefined,
                soul_root: soulRoot || undefined
            };

            // 2. Perform kabbalistic calculations
            const calcResult = calculateMap(calcInput as CalculationInput);

            // 3. Create map and apply the 'אני' label
            const resultMutation = await createMap({
                input: calcInput as CalculationInput,
                result: calcResult,
                notes: null,
                labels: ['אני'],
                gender: gender || undefined,
                soulRoot: soulRoot || undefined
            });

            const newMapId = resultMutation.id;

            // 4. Assign new map as personal profile identity
            await setAsMe(newMapId);

            toast.success('מפת הלידה האישית שלך נוצרה ושויכה בהצלחה!');
            onClose();
        } catch (err) {
            console.error('Error creating self map:', err);
            toast.error('שגיאה בשמירת מפת הלידה האישית. אנא נסה שוב.');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-md p-4">
            <div className="bg-[#0f0a24]/95 border border-gold-500/30 rounded-3xl p-6 max-w-lg w-full shadow-2xl relative overflow-hidden flex flex-col max-h-[90vh]" dir="rtl">

                {/* Background decorative glows */}
                <div className="absolute top-[-50px] left-[-50px] w-48 h-48 bg-purple-600/10 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute bottom-[-50px] right-[-50px] w-48 h-48 bg-gold-600/5 rounded-full blur-3xl pointer-events-none" />

                {/* Close Button */}
                <button
                    onClick={onClose}
                    className="absolute top-4 left-4 text-mystic-400 hover:text-white transition-colors z-20"
                >
                    <X size={22} />
                </button>

                {/* Loading State Overlay */}
                {loading && (
                    <div className="absolute inset-0 bg-[#0f0a24]/80 backdrop-blur-sm z-50 flex flex-col items-center justify-center gap-4">
                        <FlaskLoader size="md" />
                        <span className="text-gold-200 text-sm font-bold animate-pulse">מחשב ומקודד מפה סימבולית...</span>
                    </div>
                )}

                {/* Header Title */}
                <div className="text-center mb-6 shrink-0">
                    <div className="w-12 h-12 bg-gradient-to-r from-gold-500/20 to-purple-500/20 rounded-2xl flex items-center justify-center mx-auto mb-3 border border-gold-500/30 shadow-inner">
                        <User className="text-gold-400" size={24} />
                    </div>
                    <h3 className="text-xl font-black text-white bg-clip-text bg-gradient-to-r from-white via-mystic-100 to-gold-200">
                        {view === 'suggestions' ? 'האם מצאנו את מפת הלידה שלך?' :
                            view === 'manual_select' ? 'בחירת מפה קיימת' :
                                'יצירת מפת לידה אישית'}
                    </h3>
                    <p className="text-mystic-400 text-xs mt-1">
                        כדי לפתוח את הייעוץ האישי והתאמת המפות, עלינו להגדיר תחילה את מפת הלידה שלך במערכת.
                    </p>
                </div>

                {/* Scrollable Container */}
                <div className="overflow-y-auto custom-scrollbar flex-1 px-1">

                    {/* View: Initial loading/checking */}
                    {view === 'checking' && (
                        <div className="py-12 flex flex-col items-center justify-center gap-4">
                            <FlaskLoader size="sm" />
                            <p className="text-mystic-400 text-sm">מחפש התאמות במפות השמורות שלך...</p>
                        </div>
                    )}

                    {/* View: Suggestions list */}
                    {view === 'suggestions' && (
                        <div className="space-y-4">
                            <div className="bg-gold-500/5 border border-gold-500/20 rounded-xl p-3.5 flex items-start gap-3">
                                <AlertCircle className="text-gold-400 shrink-0 mt-0.5" size={18} />
                                <p className="text-mystic-300 text-xs leading-relaxed">
                                    מצאנו מפות בשם דומה לשם החשבון שלך. שיוך מפה קיימת יחסוך לך הזנה חוזרת של נתוני לידה!
                                </p>
                            </div>

                            <div className="space-y-2">
                                {suggestions.map(map => (
                                    <button
                                        key={map.id}
                                        onClick={() => handleClaimMap(map)}
                                        className="w-full text-right bg-white/[0.02] hover:bg-gold-500/10 border border-white/10 hover:border-gold-500/40 rounded-2xl p-4 flex items-center justify-between group transition-all"
                                    >
                                        <div>
                                            <div className="font-bold text-white group-hover:text-gold-300 transition-colors text-sm">
                                                {map.firstName} {map.familyName}
                                            </div>
                                            <div className="text-xs text-mystic-400 mt-1 flex items-center gap-2">
                                                <span>תאריך: {formatForDisplay(map.birthDate)}</span>
                                                {map.birth_location && <span>• מיקום: {map.birth_location}</span>}
                                            </div>
                                        </div>
                                        <div className="px-3 py-1.5 rounded-xl bg-gold-500/10 text-gold-300 text-xs font-bold border border-gold-500/25 group-hover:bg-gold-500 group-hover:text-black transition-all">
                                            זו המפה שלי
                                        </div>
                                    </button>
                                ))}
                            </div>

                            <div className="flex flex-col gap-2 pt-4">
                                <button
                                    onClick={() => setView('manual_select')}
                                    className="w-full py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-mystic-200 text-xs font-bold transition-all"
                                >
                                    חפש במפות שמורות אחרות
                                </button>
                                <button
                                    onClick={() => setView('create_form')}
                                    className="w-full py-2.5 text-mystic-400 hover:text-white text-xs underline underline-offset-4 transition-all"
                                >
                                    אף אחת מאלה לא שלי - בוא ניצור מפה חדשה
                                </button>
                            </div>
                        </div>
                    )}

                    {/* View: Manual selection from all saved maps */}
                    {view === 'manual_select' && (
                        <div className="space-y-4">
                            <div className="relative">
                                <input
                                    type="text"
                                    placeholder="חפש לפי שם..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full pl-4 pr-10 py-2.5 bg-white/5 border border-white/10 rounded-xl focus:ring-2 focus:ring-gold-500/50 focus:border-gold-500 transition-all text-white text-xs"
                                />
                                <Search className="absolute top-1/2 right-3 -translate-y-1/2 text-mystic-500" size={16} />
                            </div>

                            <div className="space-y-2 max-h-60 overflow-y-auto custom-scrollbar">
                                {isMapsLoading ? (
                                    <div className="py-8 flex justify-center"><FlaskLoader size="sm" /></div>
                                ) : filteredMaps.length > 0 ? (
                                    filteredMaps.map(map => (
                                        <button
                                            key={map.id}
                                            onClick={() => handleClaimMap(map)}
                                            className="w-full text-right bg-white/[0.01] hover:bg-gold-500/5 border border-white/5 hover:border-gold-500/20 rounded-xl p-3 flex items-center justify-between group transition-all"
                                        >
                                            <div>
                                                <div className="font-bold text-white group-hover:text-gold-200 text-xs">
                                                    {map.firstName} {map.familyName}
                                                </div>
                                                <div className="text-[11px] text-mystic-400 mt-0.5">
                                                    {formatForDisplay(map.birthDate)} {map.birth_location ? `• ${map.birth_location}` : ''}
                                                </div>
                                            </div>
                                            <span className="text-[11px] text-gold-400 underline opacity-0 group-hover:opacity-100 transition-opacity">
                                                לבחור מפה זו
                                            </span>
                                        </button>
                                    ))
                                ) : (
                                    <p className="text-center text-xs text-mystic-500 py-6">לא נמצאו מפות שמורות התואמות לחיפוש.</p>
                                )}
                            </div>

                            <div className="flex justify-between items-center pt-2">
                                <button
                                    onClick={() => setView(suggestions.length > 0 ? 'suggestions' : 'create_form')}
                                    className="flex items-center gap-1 text-mystic-400 hover:text-white text-xs font-bold transition-all"
                                >
                                    <ArrowRight size={14} />
                                    חזור
                                </button>
                                <button
                                    onClick={() => setView('create_form')}
                                    className="text-gold-400 hover:text-gold-300 text-xs font-bold transition-all"
                                >
                                    ליצור מפה חדשה
                                </button>
                            </div>
                        </div>
                    )}

                    {/* View: Full 8-Field Creation Form */}
                    {view === 'create_form' && (
                        <form onSubmit={handleCreateSelfMap} className="space-y-5">
                            <div className="bg-[#0a0514]/40 border border-gold-500/20 rounded-2xl p-4 flex items-start gap-3 backdrop-blur-md select-none">
                                <Info className="text-gold-400 shrink-0 mt-0.5" size={16} />
                                <p className="text-mystic-300 text-xs leading-relaxed text-right">
                                    הזנת כל השדות מבטיחה דיוק של 100% בחישובים האסוטריים.
                                </p>
                            </div>

                            <div className="space-y-4 text-right">
                                {/* Row 1: First Name & Family Name */}
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-1.5">
                                        <label className="flex items-center gap-1.5 text-[11px] font-bold text-mystic-200 select-none">
                                            <User size={12} className="text-gold-500/70" />
                                            שם פרטי <span className="text-red-500">*</span>
                                            <InfoTooltip content={EXPLANATIONS.LEVEL_1.FIRST_NAME} />
                                        </label>
                                        <input
                                            type="text"
                                            required
                                            value={firstName}
                                            onChange={handleNameChange(setFirstName)}
                                            placeholder="שם פרטי מלא"
                                            className="w-full px-4 rounded-xl bg-[#0a0514]/60 border border-white/10 text-white placeholder-mystic-500/50 focus:ring-2 focus:ring-gold-500/30 focus:border-gold-500 transition-all duration-200 text-xs h-[38px]"
                                            dir="rtl"
                                        />
                                    </div>

                                    <div className="space-y-1.5">
                                        <label className="flex items-center gap-1.5 text-[11px] font-bold text-mystic-200 select-none">
                                            <User size={12} className="text-gold-500/70" />
                                            שם משפחה <span className="text-red-500">*</span>
                                            <InfoTooltip content={EXPLANATIONS.LEVEL_1.FAMILY_NAME} />
                                        </label>
                                        <input
                                            type="text"
                                            required
                                            value={familyName}
                                            onChange={handleNameChange(setFamilyName)}
                                            placeholder="שם משפחה"
                                            className="w-full px-4 rounded-xl bg-[#0a0514]/60 border border-white/10 text-white placeholder-mystic-500/50 focus:ring-2 focus:ring-gold-500/30 focus:border-gold-500 transition-all duration-200 text-xs h-[38px]"
                                            dir="rtl"
                                        />
                                    </div>
                                </div>

                                {/* Row 2: Mother's Name & Date of Birth */}
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-1.5">
                                        <label className="flex items-center gap-1.5 text-[11px] font-bold text-mystic-200 select-none">
                                            <User size={12} className="text-gold-500/70" />
                                            שם האם <span className="text-red-500">*</span>
                                            <InfoTooltip content={EXPLANATIONS.LEVEL_1.MOTHER_NAME} />
                                        </label>
                                        <input
                                            type="text"
                                            required
                                            value={motherName}
                                            onChange={handleNameChange(setMotherName)}
                                            placeholder="שם פרטי מלא של האם"
                                            className="w-full px-4 rounded-xl bg-[#0a0514]/60 border border-white/10 text-white placeholder-mystic-500/50 focus:ring-2 focus:ring-gold-500/30 focus:border-gold-500 transition-all duration-200 text-xs h-[38px]"
                                            dir="rtl"
                                        />
                                    </div>

                                    <div className="space-y-1.5">
                                        <label className="flex items-center gap-1.5 text-[11px] font-bold text-mystic-200 select-none">
                                            <Calendar size={12} className="text-gold-500/70" />
                                            תאריך לידה <span className="text-red-500">*</span>
                                            <InfoTooltip content={EXPLANATIONS.LEVEL_1.BIRTH_DATE} />
                                        </label>
                                        <input
                                            type="date"
                                            required
                                            value={birthDate}
                                            onChange={(e) => setBirthDate(e.target.value)}
                                            className="w-full px-4 rounded-xl bg-[#0a0514]/60 border border-white/10 text-white placeholder-mystic-500/50 focus:ring-2 focus:ring-gold-500/30 focus:border-gold-500 transition-all duration-200 text-xs text-right h-[38px]"
                                        />
                                    </div>
                                </div>

                                {/* Row 3: Birth City & Birth Time */}
                                <div className="grid grid-cols-2 gap-4 pt-1">
                                    <div className="space-y-1.5">
                                        <label className="flex items-center gap-1.5 text-[11px] font-semibold text-mystic-300 block select-none">
                                            <MapPin size={12} className="text-gold-500/70" />
                                            מקום לידה
                                            <InfoTooltip content={EXPLANATIONS.LEVEL_1.LOCATION} />
                                        </label>
                                        <LocationSelector
                                            onLocationSelect={handleLocationSelect}
                                            defaultValue={cityName}
                                            className="w-full text-xs text-right"
                                            theme="dark"
                                        />
                                        {cityName && (
                                            <p className="text-[10px] text-gold-400 mt-1 flex items-center gap-1">
                                                <MapPin size={10} />
                                                עיר נבחרה: {cityName}
                                            </p>
                                        )}
                                    </div>

                                    <div className="space-y-1.5">
                                        <label className="flex items-center gap-1.5 text-[11px] font-semibold text-mystic-300 block select-none">
                                            <Clock size={12} className="text-gold-500/70" />
                                            שעת לידה
                                            <InfoTooltip content={EXPLANATIONS.LEVEL_1.BIRTH_TIME} />
                                        </label>
                                        <TimeSelector
                                            value={birthTime}
                                            onChange={setBirthTime}
                                            className="w-full text-xs text-right"
                                            theme="dark"
                                        />
                                    </div>
                                </div>

                                {/* Row 4: Gender & Soul Root */}
                                <div className="grid grid-cols-2 gap-4 pt-1">
                                    {/* Gender Cycle Selector */}
                                    <div className="space-y-1.5">
                                        <label className="flex items-center gap-1.5 text-[11px] font-semibold text-mystic-300 block select-none">
                                            <User size={12} className="text-gold-500/70" />
                                            מגדר <span className="text-red-500">*</span>
                                            <InfoTooltip content="הבחירה משפיעה על פניית המערכת בלשון זכר/נקבה ועל חישובים מגדריים" />
                                        </label>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setGender(prev => {
                                                    if (prev === '') return 'male';
                                                    if (prev === 'male') return 'female';
                                                    return '';
                                                });
                                            }}
                                            className="w-full px-3 bg-[#0a0514]/60 border border-white/10 hover:border-gold-500/30 rounded-xl text-xs text-center text-white font-bold transition-all duration-300 hover:bg-white/10 flex items-center justify-center gap-1 cursor-pointer active:scale-95 select-none h-[38px]"
                                        >
                                            {gender === '' && <span className="text-zinc-500">טרם נקבע</span>}
                                            {gender === 'male' && <span className="text-transparent bg-clip-text bg-gradient-to-l from-gold-400 to-amber-300">זכר</span>}
                                            {gender === 'female' && <span className="text-transparent bg-clip-text bg-gradient-to-l from-gold-400 to-amber-300">נקבה</span>}
                                        </button>
                                    </div>

                                    {/* Soul Root Cycle Selector */}
                                    <div className="space-y-1.5">
                                        <label className="flex items-center gap-1.5 text-[11px] font-semibold text-mystic-300 block select-none">
                                            <Sparkles size={12} className="text-gold-500/70" />
                                            שורש נשמה
                                            <InfoTooltip content="השייכות לשורש קין או הבל קובעת את אופי שמירת המפה מפני אסונות, ואת הזיקה לעמל ורוחניות" />
                                        </label>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setSoulRoot(prev => {
                                                    if (prev === '') return 'cain';
                                                    if (prev === 'cain') return 'abel';
                                                    return '';
                                                });
                                            }}
                                            className="w-full px-3 bg-[#0a0514]/60 border border-white/10 hover:border-gold-500/30 rounded-xl text-xs text-center text-white font-bold transition-all duration-300 hover:bg-white/10 flex items-center justify-center gap-1 cursor-pointer active:scale-95 select-none h-[38px]"
                                        >
                                            {soulRoot === '' && <span className="text-zinc-500">טרם נקבע</span>}
                                            {soulRoot === 'cain' && <span className="text-transparent bg-clip-text bg-gradient-to-l from-gold-400 to-amber-300">שורש קין</span>}
                                            {soulRoot === 'abel' && <span className="text-transparent bg-clip-text bg-gradient-to-l from-gold-400 to-amber-300">שורש הבל</span>}
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="flex items-center justify-between pt-4 border-t border-white/5">
                                {suggestions.length > 0 ? (
                                    <button
                                        type="button"
                                        onClick={() => setView('suggestions')}
                                        className="flex items-center gap-1 text-mystic-400 hover:text-white text-xs font-bold transition-all"
                                    >
                                        <ArrowRight size={14} />
                                        חזור למפות המוצעות
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={() => setView('manual_select')}
                                        className="flex items-center gap-1 text-mystic-400 hover:text-white text-xs font-bold transition-all"
                                    >
                                        <ArrowRight size={14} />
                                        חזור לחיפוש מפות
                                    </button>
                                )}

                                <button
                                    type="submit"
                                    className="py-2.5 px-6 bg-gradient-to-r from-gold-600 to-gold-500 hover:from-gold-500 hover:to-gold-400 text-white text-xs font-bold rounded-xl shadow-lg hover:shadow-gold-500/20 transition-all flex items-center gap-1.5"
                                >
                                    <span>צור ושייך את המפה שלי</span>
                                    <Sparkles size={14} className="text-gold-200 animate-pulse" />
                                </button>
                            </div>
                        </form>
                    )}

                </div>

            </div>
        </div>
    );
};
